import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Selo } from '../../componentes/base';
import { Vazio } from '../../componentes/estados';
import { Selecao } from '../../componentes/formulario';
import { Tabela, type Coluna as ColunaTabela } from '../../componentes/tabela';
import { getTodos } from '../../nucleo/api';
import { data, textoLivre } from '../../nucleo/formato';
import { CHAVE, COR_PRIORIDADE, useCardAberto, useOpcoesModuloAlvo, useQuadros, useRotuloColuna, useRotuloModuloAlvo, useRotuloQuadro, useTempoRealTarefas } from './comum';
import { DrawerCard } from './DrawerCard';
import { PRIORIDADES, TIPOS, type Card } from './tipos';

const ESTADOS = ['abertos', 'concluidos', 'todos'] as const;

/** Colunas da tabela de cards, comuns à Lista e a Minhas tarefas. */
export function useColunasCards(): ColunaTabela<Card>[] {
  const { t } = useTranslation('tarefas');
  const rotuloColuna = useRotuloColuna();
  const rotuloQuadro = useRotuloQuadro();
  const rotuloAlvo = useRotuloModuloAlvo();
  return [
    { chave: 'numero', rotulo: t('campos.numero'), mono: true, ordenavel: true, render: (c) => `#${c.numero}` },
    {
      chave: 'titulo',
      rotulo: t('campos.titulo'),
      principal: true,
      ordenavel: true,
      render: (c) => (
        <span className="flex flex-wrap items-center gap-2">
          <span className="font-medium">{textoLivre(c.titulo)}</span>
          {c.atrasado && <Selo cor="perigo">{t('kanban.atrasado')}</Selo>}
        </span>
      ),
    },
    { chave: 'coluna_nome', rotulo: t('campos.coluna'), render: (c) => rotuloColuna({ nome: c.coluna_nome, codigo: c.coluna_codigo }) },
    { chave: 'quadro_nome', rotulo: t('campos.quadro'), oculta: true, render: (c) => rotuloQuadro({ nome: c.quadro_nome, codigo: c.quadro_codigo }) },
    { chave: 'tipo', rotulo: t('campos.tipo'), render: (c) => (c.tipo ? `${c.tipo} · ${t(`tipos.${c.tipo}`)}` : '') },
    { chave: 'prioridade', rotulo: t('campos.prioridade'), ordenavel: true, render: (c) => <Selo cor={COR_PRIORIDADE[c.prioridade] ?? 'neutro'}>{c.prioridade}</Selo> },
    { chave: 'tamanho', rotulo: t('campos.tamanho'), render: (c) => (c.tamanho ? t(`tamanhos.${c.tamanho}`) : '') },
    { chave: 'modulo_alvo', rotulo: t('campos.moduloAlvo'), render: (c) => rotuloAlvo(c.modulo_alvo) },
    { chave: 'responsavel_nome', rotulo: t('campos.responsavel') },
    { chave: 'prazo', rotulo: t('campos.prazo'), mono: true, ordenavel: true, render: (c) => <span className={c.atrasado ? 'text-perigo' : ''}>{c.prazo ? data(c.prazo) : ''}</span> },
    { chave: 'origem', rotulo: t('campos.origem'), oculta: true, render: (c) => t(`origens.${c.origem}`) },
  ];
}

/** Aba Lista: todos os cards, com filtros por quadro, tipo, prioridade, módulo alvo, responsável e situação. */
export function Lista() {
  const { t } = useTranslation('tarefas');
  useTempoRealTarefas();
  const rotuloQuadro = useRotuloQuadro();
  const alvos = useOpcoesModuloAlvo();
  const quadros = useQuadros();
  const membros = useQuery({ queryKey: ['membros', 'todos'], queryFn: () => getTodos<{ id: string; nome: string }>('/membros') });
  const [cardAberto, abrir] = useCardAberto();
  const [quadro, setQuadro] = useState('');
  const [tipo, setTipo] = useState('');
  const [prioridade, setPrioridade] = useState('');
  const [alvo, setAlvo] = useState('');
  const [responsavel, setResponsavel] = useState('');
  const [estado, setEstado] = useState<(typeof ESTADOS)[number]>('abertos');
  const colunas = useColunasCards();
  const todasColunas = (quadros.data?.itens ?? []).flatMap((q) => q.colunas);
  return (
    <>
      <Tabela<Card>
        tela="tarefas.lista"
        caminho="/tarefas/cards"
        chaveConsulta={[...CHAVE, 'lista']}
        params={{ quadro_id: quadro || undefined, tipo: tipo || undefined, prioridade: prioridade || undefined, modulo_alvo: alvo || undefined, responsavel_id: responsavel || undefined, estado }}
        filtros={{ quadro, tipo, prioridade, alvo, responsavel, estado }}
        aoAplicarFiltros={(f) => {
          setQuadro(typeof f.quadro === 'string' ? f.quadro : '');
          setTipo(TIPOS.find((x) => x === f.tipo) ?? '');
          setPrioridade(PRIORIDADES.find((x) => x === f.prioridade) ?? '');
          setAlvo(typeof f.alvo === 'string' ? f.alvo : '');
          setResponsavel(typeof f.responsavel === 'string' ? f.responsavel : '');
          setEstado(ESTADOS.find((x) => x === f.estado) ?? 'abertos');
        }}
        colunas={colunas}
        aoClicar={(c) => abrir(c.id)}
        vazio={<Vazio texto={t('lista.vazio')} />}
        filtrosExtra={
          <>
            <div className="w-full sm:w-52">
              <Selecao aria-label={t('campos.quadro')} opcoes={(quadros.data?.itens ?? []).map((q) => ({ valor: q.id, rotulo: rotuloQuadro(q) }))} vazio={`${t('campos.quadro')}: ${t('kanban.todos')}`} value={quadro} onChange={(e) => setQuadro(e.target.value)} />
            </div>
            <div className="w-full sm:w-44">
              <Selecao aria-label={t('campos.situacao')} opcoes={ESTADOS.map((x) => ({ valor: x, rotulo: t(`estados.${x}`) }))} value={estado} onChange={(e) => setEstado(e.target.value as (typeof ESTADOS)[number])} />
            </div>
            <div className="w-full sm:w-44">
              <Selecao aria-label={t('campos.tipo')} opcoes={TIPOS.map((x) => ({ valor: x, rotulo: `${x} · ${t(`tipos.${x}`)}` }))} vazio={`${t('campos.tipo')}: ${t('kanban.todos')}`} value={tipo} onChange={(e) => setTipo(e.target.value)} />
            </div>
            <div className="w-full sm:w-44">
              <Selecao aria-label={t('campos.prioridade')} opcoes={PRIORIDADES.map((x) => ({ valor: x, rotulo: t(`prioridades.${x}`) }))} vazio={`${t('campos.prioridade')}: ${t('kanban.todas')}`} value={prioridade} onChange={(e) => setPrioridade(e.target.value)} />
            </div>
            <div className="w-full sm:w-48">
              <Selecao aria-label={t('campos.moduloAlvo')} opcoes={alvos} vazio={`${t('campos.moduloAlvo')}: ${t('kanban.todos')}`} value={alvo} onChange={(e) => setAlvo(e.target.value)} />
            </div>
            <div className="w-full sm:w-48">
              <Selecao aria-label={t('campos.responsavel')} opcoes={(membros.data?.itens ?? []).map((m) => ({ valor: m.id, rotulo: m.nome }))} vazio={`${t('campos.responsavel')}: ${t('kanban.todos')}`} value={responsavel} onChange={(e) => setResponsavel(e.target.value)} />
            </div>
          </>
        }
      />
      {cardAberto && <DrawerCard key={cardAberto} id={cardAberto} colunas={todasColunas} aoFechar={() => abrir(null)} />}
    </>
  );
}
